import React, { useState } from 'react';
import { cn } from '@/shared/utils/utils';
import {
  TooltipContent,
  Tooltip,
  TooltipProvider,
  TooltipTrigger,
} from '@/shared/components/ui';

export interface UserSelectorAvatarProps {
  fullName: string;
  imageUrl?: string;
  className?: string;
  size?: 'xs' | 'sm' | 'sm-md' | 'md' | 'lg' | 'xl' | '2xl' | 'auto';
  variant?: 'default' | 'solid' | 'neutral';
  shape?: 'circle' | 'square';
}

const sizeClasses = {
  xs: 'w-5 h-5 text-[9px]',
  sm: 'w-6 h-6 text-xs',
  'sm-md': 'w-7 h-7 text-sm',
  md: 'w-10 h-10 text-sm',
  lg: 'w-12 h-12 text-base',
  xl: 'w-16 h-16 text-xl',
  '2xl': 'w-24 h-24 text-2xl',
};

const variantClasses = {
  default: {
    bg: 'bg-gradient-to-br from-primary/20 via-primary/15 to-primary/10',
    ring: 'ring-2 ring-primary/20',
    text: 'text-primary',
  },
  solid: {
    bg: 'bg-blue-500',
    ring: 'border border-gray-300',
    text: 'text-white',
  },
  neutral: {
    bg: 'bg-gray-200',
    ring: 'border-2 border-white',
    text: 'text-gray-600',
  },
};

export const UserSelectorAvatar: React.FC<UserSelectorAvatarProps> = ({
  imageUrl,
  fullName,
  className,
  size = 'md',
  variant = 'default',
  shape = 'circle',
}) => {
  const [imageError, setImageError] = useState(false);

  const initials = fullName
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

  const showInitials = !imageUrl || imageError;
  const colors = variantClasses[variant] || variantClasses.default;

  // Handle auto size (100% width/height, parent controls size)
  const isAutoSize = size === 'auto';

  // Handle predefined size classes
  const sizeClass = isAutoSize
    ? 'w-full h-full'
    : sizeClasses[size as keyof typeof sizeClasses] || sizeClasses.md;
  const [sizeWidth, sizeHeight, fontSize] = sizeClass.split(' ');

  // Use larger font for auto size
  const textSize = isAutoSize ? 'text-xl' : fontSize;

  const shapeClass = shape === 'square' ? 'rounded-lg' : 'rounded-full';

  if (showInitials) {
    return (
      <div
        className={cn(
          'flex items-center justify-center',
          shapeClass,
          colors.bg,
          colors.ring,
          sizeClass,
          className
        )}
      >
        <span className={cn('font-semibold', colors.text, textSize)}>
          {initials}
        </span>
      </div>
    );
  }

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <img
            src={imageUrl}
            alt={fullName}
            onError={() => setImageError(true)}
            className={cn(
              'object-cover',
              shapeClass,
              colors.ring,
              sizeWidth,
              sizeHeight,
              className
            )}
          />
        </TooltipTrigger>
        <TooltipContent>
          <span className="text-sm font-medium">{fullName}</span>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};
